import { create } from "zustand";
import { preferences } from "@/app/platform";
import {
  TIMER_STORAGE_KEY,
  advancePhase,
  elapsedSec,
  parseStoredTimer,
  positionAt,
  serializeTimer,
  type TimerAnchor,
  type TimerFinishReason,
  type TimerSpec,
} from "./timer.model";

/**
 * THE CLOCK'S INTERFACE STATE — and the one piece of it that is deliberately durable.
 *
 * Everything here is interface state in the sense the architecture means: what is running,
 * what is paused, which phase is on, how many rounds have been banked. None of it came from
 * the service and none of it may.
 *
 * 🔑 THE ANCHOR IS WRITTEN THROUGH THE PREFERENCES SEAM, NEVER `localStorage`, and that is
 * the difference between a clock that survives a relaunch and one that quietly restarts an
 * athlete's workout at zero. Inside the app's web view `localStorage` is WebKit's store, and
 * WebKit may EVICT it when the device is short of space — silently, with nothing broken to
 * see. `src/app/platform/preferences.ts` is `UserDefaults` on device, which iOS does not
 * reclaim. Two other things in this codebase were moved off `localStorage` for exactly this
 * reason and the reasoning is written up in that module.
 *
 * 🔑 EVERY ACTION THAT NEEDS THE TIME TAKES IT AS AN ARGUMENT. Nothing here calls
 * `Date.now()`. The screen passes the instant it read, which is what lets the whole state
 * machine be driven by a spec with fake timers — and, more usefully, what stops two
 * timestamps taken microseconds apart in one transition disagreeing about when a pause began.
 *
 * 🔑 THE WRITES ARE ON THE TRANSITIONS, NEVER ON THE TICK. A start instant does not change
 * while the clock runs, so four writes a second would buy nothing and cost a plist flush four
 * times a second for twenty minutes. Start, pause, resume, a banked round, and the end each
 * write; finishing and discarding each remove.
 */

/**
 * WHERE THE PIECE IS IN ITS OWN LIFETIME.
 *
 * `counting` is a real state and not a flavour of `running`: the clock has not started, so
 * the cap has not started being spent, and Start means "cancel" rather than "pause".
 */
export type TimerStatus = "idle" | "counting" | "running" | "paused" | "finished";

interface TimerUiState {
  /** Which piece this clock belongs to. Two pieces must never read each other's anchor. */
  sessionId: string | null;
  status: TimerStatus;

  /** The instant the athlete tapped Start, or null. The ONLY thing that says "counting in". */
  countdownStartedAt: number | null;
  /** The wall-clock anchor, or null before the clock has started. */
  anchor: TimerAnchor | null;

  /** Which phase is on, and the elapsed second it began at — see `positionAt`. */
  phaseIndex: number;
  phaseStartedSec: number;

  rounds: number;
  /** The elapsed second each round was banked at. The last one is a for-time score. */
  splits: number[];
  /**
   * HOW THE PIECE ENDED, or null while it has not. Written by `finish` and by nothing else.
   *
   * ⚠️ ADDED BY BUD-289 AND IT IS NOT A SECOND CHANNEL — it is the one that already exists.
   * The debrief is the screen the timer pushes to, and it has to answer "did the clock stop
   * you" for the service. The clock is the only thing that knows: a cap that reached zero
   * stopped them, a structure that ran out did not, and a swipe is neither. That fact was
   * being computed, handed to a navigation callback as an argument, and dropped. Keeping it
   * beside the splits — which the debrief also reads, and which have never been anywhere else
   * — means one place holds everything the finished piece knows, addressed by `sessionId`.
   */
  finishedBy: TimerFinishReason | null;

  /** Point the clock at a piece. Resets everything: a new piece is not a resumed one. */
  arm: (sessionId: string) => void;
  /** Read a stored anchor back and resume from it, or leave the clock idle. */
  hydrate: (sessionId: string) => Promise<void>;

  beginCountdown: (now: number) => void;
  /** Tap anywhere during the countdown: start the clock NOW rather than at the end of the count. */
  skipCountdown: (now: number) => void;
  /** The countdown reached its end. The clock starts HERE, never at the tap. */
  startClock: (now: number) => void;
  cancelCountdown: () => void;

  pause: (now: number) => void;
  resume: (now: number) => void;

  /**
   * THE SPEC IS PASSED IN, NEVER HELD HERE.
   *
   * The screen already owns it — it renders from the prop on every frame — and a second copy
   * in this store would be a copy that goes stale the moment a caller swaps the piece it is
   * showing. BUD-285 turns a session's format into a `TimerSpec` asynchronously, so that
   * swap is not hypothetical: the clock would go on advancing phases against the placeholder.
   */
  advance: (spec: TimerSpec, now: number) => void;
  /** Bank a round at the current elapsed second. */
  logRound: (spec: TimerSpec, now: number) => void;

  /**
   * The cap arrived, the structure ran out, or the athlete swiped. The anchor is no longer a
   * clock, and `reason` is recorded because the debrief cannot re-derive it — see
   * `finishedBy` above.
   */
  finish: (now: number, reason: TimerFinishReason) => void;
  /** The ✕: leave without logging anything. */
  discard: () => void;
}

/**
 * Fire-and-forget, and deliberately so: a plist write that fails must not stop a workout.
 *
 * ⚠️ IT WRITES A STOPPED PIECE AS WELL AS A RUNNING ONE. `finishedBy` is what tells the two
 * apart on the way back in — see `StoredTimer` for why a finished piece is now written down
 * instead of deleted.
 */
function writeAnchor(state: TimerUiState): void {
  if (state.sessionId == null || state.anchor == null) return;
  void preferences
    .set(
      TIMER_STORAGE_KEY,
      serializeTimer({
        sessionId: state.sessionId,
        startedAt: state.anchor.startedAt,
        pausedMs: state.anchor.pausedMs,
        pausedAt: state.anchor.pausedAt,
        phaseIndex: state.phaseIndex,
        phaseStartedSec: state.phaseStartedSec,
        rounds: state.rounds,
        finishedBy: state.finishedBy,
        splits: state.splits,
      }),
    )
    .catch(() => {
      // Out of room, or a browser refusing storage. The clock on screen is unaffected; only
      // the recovery from a relaunch degrades, and it degrades to what it was before.
    });
}

function clearAnchor(): void {
  void preferences.remove(TIMER_STORAGE_KEY).catch(() => {});
}

const IDLE = {
  status: "idle" as TimerStatus,
  countdownStartedAt: null,
  anchor: null,
  phaseIndex: 0,
  phaseStartedSec: 0,
  rounds: 0,
  splits: [] as number[],
  finishedBy: null as TimerFinishReason | null,
};

export const useTimerUiStore = create<TimerUiState>((set, get) => ({
  sessionId: null,
  ...IDLE,

  /**
   * A NEW PIECE IS NOT A RESUMED ONE, and that now has to be true on DISK as well as in
   * memory: a finished piece outlives itself in the store, so re-starting the clock has to
   * drop it. Without this line an athlete who re-started a finished metcon and was killed
   * before the first write would come back to the piece they had just decided to redo.
   */
  arm: (sessionId) => {
    set({ sessionId, ...IDLE });
    clearAnchor();
  },

  /**
   * RELAUNCH RECOVERY — the whole reason the anchor is durable.
   *
   * The stored entry describes the PIECE, not the screen. An athlete whose phone was killed
   * by iOS at minute 12 is still doing burpees, so the clock comes back RUNNING and twelve
   * minutes in — the away time is real time and folding it in is the honest answer. What
   * bounds a return an hour later is the cap: `positionAt` reports the piece finished, and
   * the screen ends it on the first tick rather than showing a clock that has run away.
   *
   * A PAUSE SURVIVES AS A PAUSE. `pausedAt` is stored, so a clock put away paused comes back
   * paused and frozen at the same second — rather than banking every minute the app spent
   * closed, which is what a "running" entry with no pause instant would do.
   *
   * Anything it cannot vouch for leaves the clock idle. See `parseStoredTimer`: a wrong
   * resume is worse than no resume.
   */
  hydrate: async (sessionId) => {
    /**
     * 🚨 A FINISHED PIECE SURVIVES RE-ARMING, AND WITHOUT THIS LINE IT DID NOT.
     *
     * The reset below is unconditional and this hook is called on MOUNT — so a timer that
     * mounted again for the same session wiped `status`, `finishedBy` and the anchor. That is
     * exactly what happens on the navigation out of it: the screen finishes the piece, calls
     * `onFinished`, and the transition remounts the timer while the debrief is coming in. The
     * debrief then read an idle store, found no piece, and showed a session with no time and
     * no score on it — for a workout the athlete had just watched itself finish.
     *
     * Measured rather than reasoned about: a subscription on this store logged
     * `finished/structure` followed immediately by two `idle`s (two, because React invokes an
     * effect twice in development's strict mode — which is also why the guard has to be here,
     * in the store, rather than in the effect that calls it).
     *
     * The in-memory copy is also the FRESHER one: a finished piece is written to the store
     * now rather than deleted, so this early return keeps a reading the athlete just watched
     * stop in preference to reading the same thing back off a plist.
     */
    if (get().sessionId === sessionId && get().status === "finished") return;

    set({ sessionId, ...IDLE });

    let raw: string | null = null;
    try {
      raw = await preferences.get(TIMER_STORAGE_KEY);
    } catch {
      return;
    }

    const stored = parseStoredTimer(raw, sessionId);
    if (!stored) return;

    // Armed again in the meantime, or already started by an athlete who did not wait for a
    // read from a plist. Either way this answer is stale and applying it would rewind them.
    if (get().sessionId !== sessionId || get().status !== "idle") return;

    set({
      /**
       * 🚨 A PIECE THAT ENDED COMES BACK ENDED, NEVER RUNNING — and this is the line that
       * closes the window the metcon opened. The clock stops, the athlete goes back to their
       * session for the accessory and the cooldown, and iOS kills the app somewhere in those
       * minutes: what the debrief then reads has to be the score they earned, not a clock
       * that starts counting the walk to the rower.
       *
       * It is also the answer to the hazard that deleting the entry used to solve. A stored
       * ending cannot be "resumed by mistake", because nothing resumes it: `finishedBy` puts
       * the piece straight back into the state it was actually in.
       */
      status: stored.finishedBy != null ? "finished" : stored.pausedAt != null ? "paused" : "running",
      finishedBy: stored.finishedBy,
      anchor: {
        startedAt: stored.startedAt,
        pausedMs: stored.pausedMs,
        pausedAt: stored.pausedAt,
      },
      phaseIndex: stored.phaseIndex,
      phaseStartedSec: stored.phaseStartedSec,
      rounds: stored.rounds,
      /* The REAL round times, read back — never reconstructed from a round count, which is
         the thing the old comment here was right to refuse. See `StoredTimer.splits`. */
      splits: stored.splits,
    });
  },

  beginCountdown: (now) => set({ status: "counting", countdownStartedAt: now }),

  /**
   * SKIPPING THE COUNTDOWN STARTS THE CLOCK NOW, and does not merely shorten the count.
   *
   * The athlete who taps is already at the bar. Anything else — jumping to "1", say — would
   * be a control that acknowledges the tap and then makes them wait anyway.
   */
  skipCountdown: (now) => get().startClock(now),

  startClock: (now) => {
    set({
      status: "running",
      countdownStartedAt: null,
      anchor: { startedAt: now, pausedMs: 0, pausedAt: null },
      phaseIndex: 0,
      phaseStartedSec: 0,
    });
    writeAnchor(get());
  },

  /** An aborted start writes nothing, because nothing was ever anchored. */
  cancelCountdown: () => set({ status: "idle", countdownStartedAt: null }),

  pause: (now) => {
    const { anchor, status } = get();
    if (anchor == null || status !== "running") return;
    set({ status: "paused", anchor: { ...anchor, pausedAt: now } });
    writeAnchor(get());
  },

  /**
   * The pause that just ended is banked into `pausedMs` here — the one moment its length is
   * finally known — and `pausedAt` goes back to null, which is what makes the clock run again.
   */
  resume: (now) => {
    const { anchor, status } = get();
    if (anchor == null || anchor.pausedAt == null || status !== "paused") return;
    set({
      status: "running",
      anchor: {
        startedAt: anchor.startedAt,
        pausedMs: anchor.pausedMs + Math.max(0, now - anchor.pausedAt),
        pausedAt: null,
      },
    });
    writeAnchor(get());
  },

  advance: (spec, now) => {
    const state = get();
    if (state.anchor == null || state.status !== "running") return;
    const position = positionAt(
      spec,
      elapsedSec(state.anchor, now),
      state.phaseIndex,
      state.phaseStartedSec,
    );
    // A TIMED PHASE IS THE CLOCK'S TO END, NEVER THE ATHLETE'S. `positionAt` has already
    // advanced past it by the time this runs, so advancing again here would skip a phase on
    // every tap — the athlete would arrive at the last round of an EMOM halfway through it.
    if (position.phaseRemainingSec != null) return;
    const next = advancePhase(spec, position);
    if (next.index === state.phaseIndex) return;
    set({ phaseIndex: next.index, phaseStartedSec: next.startedSec });
    writeAnchor(get());
  },

  logRound: (spec, now) => {
    const state = get();
    if (state.anchor == null || state.status !== "running") return;
    const at = positionAt(
      spec,
      elapsedSec(state.anchor, now),
      state.phaseIndex,
      state.phaseStartedSec,
    ).elapsedSec;
    set({ rounds: state.rounds + 1, splits: [...state.splits, at] });
    writeAnchor(get());
  },

  /**
   * THE PIECE IS OVER, AND THE CLOCK STOPS WHERE IT STOPPED.
   *
   * The anchor is frozen by stamping `pausedAt`, not cleared: the elapsed time IS the score
   * on a for-time piece, and a screen that forgot it the instant the athlete finished would
   * have nothing to show or to send.
   *
   * 🚨 THE STORED COPY IS KEPT NOW, AND IT USED TO BE DELETED HERE. The reasoning for deleting
   * it was that "a finished piece is a clock waiting to be resumed by mistake" — true of an
   * entry that could only describe a RUNNING clock, which is what the stored shape was. The
   * cost of it only became visible when the metcon stopped leading straight into the debrief:
   * between the two there is now an accessory and a cooldown, the score existed in memory
   * alone, and an app iOS killed in that window lost the time and the rounds of a workout the
   * athlete had just finished. `finishedBy` travels with the reading instead, so the piece
   * comes back stopped and the mistake the deletion guarded against cannot happen.
   *
   * The copy is dropped when the session is written down (`forgetFinishedPiece`, called by the
   * debrief), when the ✕ discards, and when a new piece is armed.
   */
  finish: (now, reason) => {
    const { anchor, status } = get();
    if (status === "finished") return;
    set({
      status: "finished",
      countdownStartedAt: null,
      anchor: anchor == null ? null : { ...anchor, pausedAt: anchor.pausedAt ?? now },
      finishedBy: reason,
    });
    writeAnchor(get());
  },

  discard: () => {
    set({ ...IDLE });
    clearAnchor();
  },
}));
